import { useCallback, useEffect, useMemo, useState } from 'react';
import { authApi, portailApi } from '../../api/resources';
import { useSession } from '../../api/session';
import { fmt } from '../../lib/currency';
import { tr, trf } from '../../lib/i18n';
import { NOM_LOGICIEL } from '../../lib/marque';
import { GuideInstallation } from '../../pwa/GuideInstallation.jsx';
import { C } from '../../theme/palette';
import { CommandesAValider } from './CommandesAValider.jsx';
import { BandeauChantier, ChoixChantier, ChoixFournisseur, EtapesCommande } from './Commander.jsx';
import { MonEquipe } from './EquipeClient.jsx';
import { commandeEnCours, fournisseursDe, libelleChantier, retiensCommande } from './listes';
import { MesChantiers } from './MesChantiers.jsx';
import { MesFournisseurs } from './MesFournisseurs.jsx';
import { MonCompte } from './MonCompte.jsx';
import { ToutesMesCommandes } from './ToutesMesCommandes.jsx';
import { alerte, arrondi, bouton, carte, champ, nombre, ROLES, stockDe } from './styles';

export { PartageInvitation } from './EquipeClient.jsx';

/** Le nombre d'articles et de pièces d'un panier, sans un prix. */
const compte = (lignes) =>
  trf(
    '{0} article(s) · {1} pièce(s)',
    lignes.length,
    arrondi(lignes.reduce((s, l) => s + nombre(l.quantite), 0)),
  );

/**
 * L'espace client du magasin : ce que voit une personne d'une entreprise
 * cliente, connectée avec son propre compte.
 *
 * <p>Les onglets : « Commander » en trois étapes — le chantier (ceux de
 * l'entreprise, ou un autre avec son adresse exacte), le fournisseur, puis le
 * catalogue, le stock, un panier —, « À valider » pour le chef et le
 * responsable (les commandes des ouvriers), « Mes commandes » (toutes, chez
 * tous les fournisseurs, le bon de commande A4 et « Recommander »), et pour
 * le responsable et les chefs « Chantiers » et « Mon équipe ». L'ouvrier ne
 * voit jamais un prix : sa commande part à valider chez son chef. Le chantier
 * choisi survit au changement de magasin. Le client est vouvoyé.
 *
 * @param {object} props propriétés
 * @param {() => void} [props.recharge] recharge la page après le changement de magasin
 * @returns {import('react').ReactElement} l'espace client
 */
export function PortailClient({ recharge = () => window.location.reload() }) {
  const { deconnexion, basculeSociete } = useSession();
  const [profil, setProfil] = useState(null);
  const [erreur, setErreur] = useState('');
  const [onglet, setOnglet] = useState('commander');
  const [catalogue, setCatalogue] = useState([]);
  const [panier, setPanier] = useState({});
  // La commande en cours : son chantier, son fournisseur, son étape. Elle
  // survit au changement de magasin, qui recharge la page.
  const [suite, setSuite] = useState(commandeEnCours);
  const [chantiers, setChantiers] = useState(null);
  const [commandes, setCommandes] = useState(null);
  const [societes, setSocietes] = useState([]);
  // La quantité qu'on est en train de taper : son champ reste là, même vidé
  // le temps d'écrire la nouvelle.
  const saisie = useState(null);
  const enSaisie = saisie[0];

  const rechargeCatalogue = useCallback(
    () =>
      portailApi
        .catalogue()
        .then((l) => setCatalogue(Array.isArray(l) ? l : []))
        .catch(() => {}),
    [],
  );
  const rechargeCommandes = useCallback(
    () =>
      portailApi
        .commandes()
        .then((l) => setCommandes(Array.isArray(l) ? l : []))
        .catch(() => setCommandes((c) => c || [])),
    [],
  );
  const rechargeChantiers = useCallback(
    () =>
      Promise.resolve()
        .then(() => portailApi.chantiers())
        .then((l) => setChantiers(Array.isArray(l) ? l : []))
        .catch(() => setChantiers((c) => c || [])),
    [],
  );

  useEffect(() => {
    portailApi
      .moi()
      .then(setProfil)
      .catch((e) => setErreur(e?.message || 'Votre espace client n’a pas pu s’ouvrir.'));
    rechargeCatalogue();
    // Les commandes dès l'ouverture : le nombre de commandes à valider, sur
    // l'onglet.
    rechargeCommandes();
    authApi
      .societes()
      .then((l) => setSocietes(Array.isArray(l) ? l : []))
      .catch(() => {});
  }, [rechargeCatalogue, rechargeCommandes]);

  useEffect(() => {
    if (onglet === 'avalider') rechargeCommandes();
    if (onglet === 'commander') rechargeChantiers();
  }, [onglet, rechargeCommandes, rechargeChantiers]);

  const devise = profil?.magasin?.devise || 'CHF';
  const voitLesPrix = !!profil?.voitLesPrix;
  const parReference = useMemo(
    () => Object.fromEntries(catalogue.map((a) => [a.reference, a])),
    [catalogue],
  );
  const lignesPanier = Object.entries(panier)
    .filter(([ref, q]) => q > 0 || ref === enSaisie)
    .map(([ref, q]) => ({ article: parReference[ref], quantite: q }))
    .filter((l) => l.article);
  const totalPanier = lignesPanier.reduce(
    (s, l) => s + l.quantite * Number(l.article.prixNet || 0),
    0,
  );
  // Jamais plus que ce que le magasin a encore : le serveur le refuserait.
  const borne = (ref, q) => Math.min(stockDe(parReference[ref]), Math.max(0, arrondi(q)));
  const ajoute = (ref, delta) =>
    setPanier((p) => ({ ...p, [ref]: borne(ref, (p[ref] || 0) + delta) }));
  const fixe = (ref, q) => setPanier((p) => ({ ...p, [ref]: borne(ref, Number(q) || 0) }));

  const aValider = (commandes || []).filter((c) => c.peutValider);

  const majSuite = (etat) => {
    setSuite(etat);
    retiensCommande(etat);
  };
  const magasinId = profil?.magasinId ?? null;

  const recommande = (commande) => {
    const p = {};
    (commande.lignes || []).forEach((l) => {
      const article = parReference[l.reference];
      if (!l.reference || !article || stockDe(article) <= 0) return;
      p[l.reference] = Math.min(stockDe(article), nombre(l.quantite) || 1);
    });
    setPanier(p);
    // Le chantier se choisit à nouveau — la même marchandise ne repart pas
    // forcément au même endroit —, chez ce même magasin.
    majSuite({ fournisseurId: magasinId, garde: true, etape: 'chantier' });
    setOnglet('commander');
  };

  if (erreur) {
    return (
      <div style={{ minHeight: '100vh', background: '#F3F4F6', padding: 20 }}>
        <div style={{ ...carte, textAlign: 'center' }}>
          <div style={{ fontSize: 30 }}>🔒</div>
          <p style={{ fontSize: 15 }}>{erreur}</p>
          <button style={bouton(C.navy)} onClick={deconnexion}>
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  const { fournisseurs, entreprises } = fournisseursDe(societes);
  const peutChanger = fournisseurs.length >= 2 || entreprises.length > 0;
  const actif = (cle) => onglet === cle || (cle === 'commander' && onglet === 'panier');

  // L'étape de la commande : le chantier, puis le fournisseur (s'il y en a
  // plusieurs), puis les articles du magasin ouvert.
  const plusieurs = fournisseurs.length >= 2;
  let etape = 'articles';
  if (!suite.chantier || suite.etape === 'chantier') etape = 'chantier';
  else if (
    plusieurs &&
    !(suite.etape === 'articles' && (magasinId == null || suite.fournisseurId === magasinId))
  )
    etape = 'fournisseur';
  const choisitChantier = (ch) => {
    const direct = suite.garde || !plusieurs;
    majSuite({
      chantier: ch,
      fournisseurId: direct ? magasinId : null,
      etape: direct ? 'articles' : 'fournisseur',
    });
    if (suite.garde && lignesPanier.length > 0) setOnglet('panier');
  };
  const choisitFournisseur = async (s) => {
    const avant = suite;
    majSuite({ ...suite, fournisseurId: s.id, etape: 'articles' });
    if (s.courante || s.id === magasinId) return;
    try {
      await basculeSociete(s.id);
    } catch (e) {
      majSuite({ ...avant, etape: 'fournisseur' });
      throw e;
    }
    // Chaque magasin a son catalogue, ses prix : la page repart, le
    // chantier retenu.
    recharge();
  };
  const versEtape = (cle) => {
    majSuite({ ...suite, etape: cle });
    setOnglet('commander');
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F3F4F6', paddingBottom: 96, color: C.ink }}>
      <header style={{ background: C.navy, color: '#fff', padding: '12px 16px' }}>
        <div
          // Sur un petit téléphone, les boutons passent sous le nom plutôt
          // que de couper « Matériaux » en deux.
          style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
        >
          {profil?.magasin?.logo ? (
            <img
              src={profil.magasin.logo}
              alt=""
              style={{
                height: 34,
                maxWidth: 80,
                objectFit: 'contain',
                background: '#fff',
                borderRadius: 6,
                padding: 2,
              }}
            />
          ) : (
            <span aria-hidden="true" style={{ fontSize: 24 }}>
              🏬
            </span>
          )}
          <div style={{ flex: '1 1 150px', minWidth: 0 }}>
            <div
              style={{
                // Le nom du magasin en entier, sur deux lignes au besoin : à
                // côté des trois boutons, il ne tenait plus sur une seule.
                fontSize: 15,
                fontWeight: 900,
                lineHeight: 1.2,
                // Coupé entre les mots, jamais au milieu (« Matériau/x »).
                overflowWrap: 'break-word',
              }}
            >
              {profil?.magasin?.nom || 'Magasin'}
            </div>
            <div
              style={{
                fontSize: 12,
                opacity: 0.8,
                lineHeight: 1.3,
                // Entreprise, nom et rôle sur deux lignes au plus, au lieu d'un
                // « Rossi Constructi… » qui cachait la personne.
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {profil
                ? profil.client + ' · ' + [profil.prenom, profil.nom].filter(Boolean).join(' ')
                : '…'}
              {profil?.role && ROLES[profil.role] ? ' · ' + tr(ROLES[profil.role]) : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', flexShrink: 0 }}>
            {peutChanger && (
              <button
                onClick={() => setOnglet('fournisseurs')}
                aria-pressed={onglet === 'fournisseurs'}
                style={{
                  ...bouton('#fff', false),
                  padding: '8px 8px',
                  fontSize: 12,
                  background: 'transparent',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,.5)',
                  whiteSpace: 'nowrap',
                }}
              >
                ⇄ Changer
              </button>
            )}
            <button
              onClick={() => setOnglet('compte')}
              aria-label="Mon compte"
              aria-pressed={onglet === 'compte'}
              style={{
                ...bouton('#fff', false),
                padding: '8px 8px',
                minWidth: 44,
                fontSize: 16,
                background: onglet === 'compte' ? 'rgba(255,255,255,.18)' : 'transparent',
                color: '#fff',
                border: '1px solid rgba(255,255,255,.5)',
              }}
            >
              👤
            </button>
            <button
              onClick={deconnexion}
              aria-label="Se déconnecter"
              style={{
                ...bouton('#fff', false),
                padding: '7px 10px',
                fontSize: 12,
                background: 'transparent',
                color: '#fff',
                border: '1px solid rgba(255,255,255,.4)',
              }}
            >
              Quitter
            </button>
          </div>
        </div>
      </header>

      <nav
        style={{
          display: 'flex',
          gap: 6,
          padding: '10px 12px 0',
          maxWidth: 640,
          margin: '0 auto',
          // Sur un écran plus étroit que les cinq onglets, on les fait glisser
          // plutôt que de les laisser se chevaucher.
          overflowX: 'auto',
        }}
      >
        {[
          ['commander', '🛒', 'Commander'],
          ...(profil?.valide ? [['avalider', '✅', 'À valider']] : []),
          ['commandes', '📦', 'Mes commandes'],
          ...(profil?.valide
            ? [
                ['chantiers', '🏗', 'Chantiers'],
                ['equipe', '👷', 'Mon équipe'],
              ]
            : []),
        ].map(([cle, icone, lib]) => (
          <button
            key={cle}
            onClick={() => setOnglet(cle)}
            aria-pressed={actif(cle)}
            style={{
              ...bouton(C.navy, actif(cle)),
              // Jusqu'à cinq onglets côte à côte sur un iPhone : l'icône
              // au-dessus du libellé, pour que « Mon équipe » ne sorte pas de
              // l'écran.
              flex: '1 1 0',
              // Jamais plus étroit que son mot le plus long : à 0, « Commander »
              // et « commandes » débordaient de leur cadre sur un iPhone.
              minWidth: 'min-content',
              padding: '7px 2px',
              fontSize: 11.5,
              lineHeight: 1.2,
              // « Mes commandes » passe sur deux lignes plutôt que d'être coupé.
              whiteSpace: 'normal',
              position: 'relative',
            }}
          >
            <span aria-hidden="true" style={{ display: 'block', fontSize: 17 }}>
              {icone}
            </span>
            {lib}
            {cle === 'avalider' && aValider.length > 0 && (
              <span
                data-testid="nombre-a-valider"
                style={{
                  position: 'absolute',
                  top: 3,
                  right: 6,
                  minWidth: 20,
                  height: 20,
                  borderRadius: 10,
                  background: C.red,
                  color: '#fff',
                  fontSize: 11.5,
                  fontWeight: 900,
                  lineHeight: '20px',
                  padding: '0 5px',
                  boxSizing: 'border-box',
                }}
              >
                {aValider.length}
              </span>
            )}
          </button>
        ))}
      </nav>

      <main style={{ padding: 12, maxWidth: 640, margin: '0 auto' }}>
        <GuideInstallation
          vous
          replie
          nom={profil?.magasin?.nom || NOM_LOGICIEL}
          apres="Vous commanderez en un geste."
        />
        {onglet === 'fournisseurs' && (
          <MesFournisseurs
            fournisseurs={fournisseurs}
            entreprises={entreprises}
            entete={
              profil
                ? profil.client + ' · ' + [profil.prenom, profil.nom].filter(Boolean).join(' ')
                : ''
            }
            basculeSociete={basculeSociete}
            recharge={recharge}
            commander={() => setOnglet('commander')}
          />
        )}
        {onglet === 'compte' && profil && (
          <MonCompte profil={profil} setProfil={setProfil} deconnexion={deconnexion} />
        )}
        {onglet === 'commander' && profil && !profil.telephone && (
          <button
            onClick={() => setOnglet('compte')}
            style={{
              ...carte,
              width: '100%',
              textAlign: 'left',
              border: '1.5px dashed ' + C.amber,
              fontFamily: 'inherit',
              fontSize: 13,
              color: C.ink,
              cursor: 'pointer',
              lineHeight: 1.45,
            }}
          >
            📞 Ajoutez votre numéro de portable : le livreur vous appelle en arrivant sur le
            chantier.
          </button>
        )}
        {(onglet === 'commander' || onglet === 'panier') && (
          <EtapesCommande etape={onglet === 'panier' ? 'articles' : etape} vers={versEtape} />
        )}
        {(onglet === 'panier' || (onglet === 'commander' && etape !== 'chantier')) &&
          suite.chantier && <BandeauChantier chantier={suite.chantier} />}
        {onglet === 'commander' && etape === 'chantier' && (
          <ChoixChantier
            chantiers={chantiers}
            telephone={profil?.telephone}
            choisit={choisitChantier}
          />
        )}
        {onglet === 'commander' && etape === 'fournisseur' && (
          <ChoixFournisseur fournisseurs={fournisseurs} choisit={choisitFournisseur} />
        )}
        {onglet === 'commander' && etape === 'articles' && (
          <Catalogue
            catalogue={catalogue}
            panier={panier}
            ajoute={ajoute}
            fixe={fixe}
            devise={devise}
            voitLesPrix={voitLesPrix}
            saisie={saisie}
          />
        )}
        {onglet === 'panier' && (
          <Panier
            lignes={lignesPanier}
            total={totalPanier}
            devise={devise}
            voitLesPrix={voitLesPrix}
            fixe={fixe}
            saisie={saisie}
            chantier={suite.chantier}
            magasin={profil?.magasin?.nom}
            retour={() => setOnglet('commander')}
            choisirChantier={() => versEtape('chantier')}
            envoyee={() => {
              setPanier({});
              rechargeCatalogue();
              rechargeCommandes();
            }}
            voirCommandes={() => setOnglet('commandes')}
          />
        )}
        {onglet === 'avalider' && profil?.valide && (
          <CommandesAValider
            commandes={commandes}
            catalogue={parReference}
            devise={devise}
            apres={() => {
              rechargeCommandes();
              rechargeCatalogue();
            }}
          />
        )}
        {onglet === 'commandes' && (
          <ToutesMesCommandes
            client={profil?.client}
            voitLesPrix={voitLesPrix}
            recommande={recommande}
          />
        )}
        {onglet === 'chantiers' && profil?.valide && <MesChantiers client={profil.client} />}
        {onglet === 'equipe' && profil?.valide && (
          <MonEquipe
            client={profil.client}
            magasin={profil.magasin?.nom}
            responsable={!!profil.responsable}
          />
        )}
      </main>

      {onglet === 'commander' && etape === 'articles' && lignesPanier.length > 0 && (
        <div
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            padding: 12,
            background: 'linear-gradient(transparent, #F3F4F6 30%)',
          }}
        >
          {suite.chantier && (
            <div
              translate="no"
              style={{
                maxWidth: 640,
                margin: '0 auto 6px',
                fontSize: 12.5,
                fontWeight: 800,
                color: C.ink,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              🏗 {libelleChantier(suite.chantier.numero, suite.chantier.nom)}
            </div>
          )}
          <button
            onClick={() => setOnglet('panier')}
            style={{
              ...bouton(C.green),
              width: '100%',
              maxWidth: 640,
              display: 'block',
              margin: '0 auto',
              fontSize: 16,
              padding: '15px 14px',
            }}
          >
            {voitLesPrix ? (
              <>
                🛒 {lignesPanier.length} article(s) · {devise} {fmt(totalPanier)} HT — Voir le
                panier ›
              </>
            ) : (
              '🛒 ' + compte(lignesPanier) + ' — ' + tr('Voir le panier ›')
            )}
          </button>
        </div>
      )}
    </div>
  );
}

/** Le catalogue, par rayon, avec la recherche et le stock ; les prix à qui les voit. */
function Catalogue({ catalogue, panier, ajoute, fixe, devise, voitLesPrix, saisie }) {
  const [enSaisie, setEnSaisie] = saisie;
  const [recherche, setRecherche] = useState('');
  const [rayon, setRayon] = useState('');
  const filtre = recherche.trim().toLowerCase();
  const rayons = Array.from(new Set(catalogue.map((a) => a.categorie || 'Divers')));
  const visibles = catalogue.filter(
    (a) =>
      (!rayon || (a.categorie || 'Divers') === rayon) &&
      (!filtre ||
        (a.nom || '').toLowerCase().includes(filtre) ||
        (a.reference || '').toLowerCase().includes(filtre) ||
        (a.categorie || '').toLowerCase().includes(filtre)),
  );
  return (
    <>
      <input
        style={{ ...champ, marginBottom: 8 }}
        aria-label="Rechercher un article"
        placeholder="🔎 Rechercher un article, une référence…"
        value={recherche}
        onChange={(e) => setRecherche(e.target.value)}
      />
      {rayons.length > 1 && (
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 10 }}>
          {['', ...rayons].map((r) => (
            <button
              key={r || 'tous'}
              onClick={() => setRayon(r)}
              aria-pressed={rayon === r}
              style={{
                ...bouton(C.teal, rayon === r),
                padding: '6px 11px',
                fontSize: 12,
                whiteSpace: 'nowrap',
              }}
            >
              {r || 'Tout'}
            </button>
          ))}
        </div>
      )}
      {visibles.length === 0 && (
        <div style={{ ...carte, textAlign: 'center', color: C.muted }}>
          Aucun article ne correspond.
        </div>
      )}
      {visibles.map((a) => {
        const q = panier[a.reference] || 0;
        const remise = Number(a.remise || 0);
        const max = stockDe(a);
        const rupture = max <= 0 || a.disponible === false;
        const auMax = !rupture && q > 0 && q >= max;
        return (
          <div
            key={a.reference}
            data-testid={'article-' + a.reference}
            style={{ ...carte, opacity: rupture ? 0.6 : 1 }}
          >
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 10,
                  background: '#F1F5F9',
                  flexShrink: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 26,
                }}
              >
                {a.image ? (
                  <img
                    src={portailApi.image(a.id)}
                    alt=""
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  '📦'
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.3 }}>{a.nom}</div>
                <div style={{ fontSize: 11.5, color: C.muted }}>
                  {a.reference} · {a.unite || 'pce'}
                </div>
                {rupture ? (
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.red }}>
                    Rupture de stock
                  </div>
                ) : (
                  max !== Infinity && (
                    <div
                      style={{ fontSize: 12, fontWeight: 700, color: max > 5 ? C.green : C.amber }}
                    >
                      {trf('en stock : {0}', max)}
                    </div>
                  )
                )}
                {voitLesPrix && a.prixNet != null && (
                  <div style={{ marginTop: 3 }}>
                    {remise > 0 && (
                      <span
                        style={{
                          fontSize: 12,
                          color: C.muted,
                          textDecoration: 'line-through',
                          marginRight: 6,
                        }}
                      >
                        {fmt(a.prixPublic)}
                      </span>
                    )}
                    <span style={{ fontSize: 15, fontWeight: 900, color: C.navy }}>
                      {devise} {fmt(a.prixNet)}
                    </span>
                    <span style={{ fontSize: 11, color: C.muted }}> HT</span>
                    {remise > 0 && (
                      <span
                        style={{ marginLeft: 6, fontSize: 11, fontWeight: 800, color: C.green }}
                      >
                        −{remise} %
                      </span>
                    )}
                  </div>
                )}
              </div>
              {rupture ? (
                <span aria-hidden="true" style={{ fontSize: 12, color: C.muted }}>
                  —
                </span>
              ) : q > 0 || enSaisie === a.reference ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button
                    aria-label={'Moins ' + a.nom}
                    onClick={() => ajoute(a.reference, -1)}
                    style={{ ...bouton(C.navy, false), padding: '6px 11px', minWidth: 44 }}
                  >
                    −
                  </button>
                  <input
                    aria-label={'Quantité ' + a.nom}
                    inputMode="decimal"
                    value={q || ''}
                    onChange={(e) => fixe(a.reference, e.target.value.replace(',', '.'))}
                    onFocus={() => setEnSaisie(a.reference)}
                    onBlur={() => setEnSaisie(null)}
                    style={{
                      width: 46,
                      minHeight: 44,
                      boxSizing: 'border-box',
                      textAlign: 'center',
                      border: '1px solid ' + C.border,
                      borderRadius: 8,
                      padding: 6,
                      fontSize: 15,
                      fontWeight: 800,
                    }}
                  />
                  <button
                    aria-label={'Plus ' + a.nom}
                    onClick={() => ajoute(a.reference, 1)}
                    disabled={auMax}
                    style={{
                      ...bouton(C.navy),
                      padding: '6px 11px',
                      minWidth: 44,
                      opacity: auMax ? 0.35 : 1,
                      cursor: auMax ? 'default' : 'pointer',
                    }}
                  >
                    +
                  </button>
                </div>
              ) : (
                <button
                  aria-label={'Ajouter ' + a.nom}
                  onClick={() => ajoute(a.reference, 1)}
                  style={{ ...bouton(C.green), padding: '10px 13px', minWidth: 44 }}
                >
                  ＋
                </button>
              )}
            </div>
            {auMax && (
              <div style={{ fontSize: 12, color: C.red, fontWeight: 700, marginTop: 6 }}>
                {trf('Maximum atteint : le magasin n’en a que {0}.', max)}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

/** Le panier : les quantités, pour le chantier choisi, et l'envoi. */
function Panier({
  lignes,
  total,
  devise,
  voitLesPrix,
  fixe,
  saisie,
  chantier,
  magasin,
  retour,
  choisirChantier,
  envoyee,
  voirCommandes,
}) {
  const [envoi, setEnvoi] = useState({ enCours: false, erreur: '' });
  const [recu, setRecu] = useState(null);
  const envoie = async () => {
    if (!chantier?.nom || !chantier?.adresse) {
      setEnvoi({ enCours: false, erreur: 'Indiquez pour quel chantier vous commandez.' });
      return;
    }
    setEnvoi({ enCours: true, erreur: '' });
    try {
      const r = await portailApi.commande({
        chantierId: chantier.id ?? null,
        chantierNumero: chantier.numero || null,
        chantier: chantier.nom,
        adresseLivraison: chantier.adresse,
        telephone: chantier.telephone || null,
        lignes: lignes
          .filter((l) => l.quantite > 0)
          .map((l) => ({ reference: l.article.reference, quantite: l.quantite })),
      });
      setRecu(r);
      envoyee();
      setEnvoi({ enCours: false, erreur: '' });
    } catch (e) {
      setEnvoi({
        enCours: false,
        erreur: e?.message || 'La commande n’est pas partie. Réessayez.',
      });
    }
  };
  if (recu) {
    const aValider = recu.statut === 'A_VALIDER';
    return (
      <div style={{ ...carte, textAlign: 'center' }}>
        <div style={{ fontSize: 40 }}>{aValider ? '⏳' : '✅'}</div>
        <h2 style={{ fontSize: 19, margin: '6px 0' }}>
          {aValider
            ? trf('Commande {0} envoyée à valider', recu.numero)
            : trf('Commande {0} envoyée', recu.numero)}
        </h2>
        <p style={{ fontSize: 14, color: C.grey, lineHeight: 1.5 }}>
          {aValider
            ? tr('Votre chef ou votre responsable la valide, puis elle part au magasin.')
            : trf('{0} l’a reçue et vous la confirmera.', magasin || tr('Le magasin'))}
          {!aValider &&
            recu.total != null &&
            ' ' + trf('Total {0} {1} HT.', devise, fmt(recu.total))}
        </p>
        <button style={{ ...bouton(C.navy), width: '100%' }} onClick={voirCommandes}>
          📦 Suivre mes commandes
        </button>
      </div>
    );
  }
  return (
    <>
      <button onClick={retour} style={{ ...bouton(C.navy, false), marginBottom: 10 }}>
        ‹ Continuer mes achats
      </button>
      {!chantier && (
        <button
          onClick={choisirChantier}
          style={{ ...bouton('#D97706'), width: '100%', marginBottom: 10 }}
        >
          🏗 Choisir le chantier
        </button>
      )}
      <div style={carte}>
        <h2 style={{ fontSize: 17, margin: '0 0 8px' }}>Votre panier</h2>
        {lignes.length === 0 && <div style={{ color: C.muted }}>Le panier est vide.</div>}
        {lignes.map((l) => (
          <div
            key={l.article.reference}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 0',
              borderBottom: '1px solid ' + C.border,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{l.article.nom}</div>
              <div style={{ fontSize: 12, color: C.muted }}>
                {voitLesPrix && l.article.prixNet != null
                  ? devise + ' ' + fmt(l.article.prixNet) + ' / ' + (l.article.unite || 'pce')
                  : l.article.unite || 'pce'}
                {stockDe(l.article) !== Infinity &&
                  ' · ' + trf('en stock : {0}', stockDe(l.article))}
              </div>
            </div>
            <input
              aria-label={'Quantité ' + l.article.nom}
              inputMode="decimal"
              value={l.quantite || ''}
              onChange={(e) => fixe(l.article.reference, e.target.value.replace(',', '.'))}
              onFocus={() => saisie[1](l.article.reference)}
              onBlur={() => saisie[1](null)}
              style={{
                width: 52,
                minHeight: 44,
                boxSizing: 'border-box',
                textAlign: 'center',
                border: '1px solid ' + C.border,
                borderRadius: 8,
                padding: 7,
                fontSize: 15,
              }}
            />
            {voitLesPrix && (
              <div style={{ width: 82, textAlign: 'right', fontWeight: 800, fontSize: 14 }}>
                {fmt(l.quantite * Number(l.article.prixNet || 0))}
              </div>
            )}
            <button
              aria-label={'Retirer ' + l.article.nom}
              onClick={() => fixe(l.article.reference, 0)}
              style={{
                border: 'none',
                background: 'none',
                fontSize: 18,
                minWidth: 44,
                minHeight: 44,
                cursor: 'pointer',
                color: C.red,
              }}
            >
              ✕
            </button>
          </div>
        ))}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 10,
            fontSize: 17,
            fontWeight: 900,
          }}
        >
          {voitLesPrix ? (
            <>
              <span>Total hors TVA</span>
              <span>
                {devise} {fmt(total)}
              </span>
            </>
          ) : (
            <span>{compte(lignes)}</span>
          )}
        </div>
        {!voitLesPrix && (
          <div
            style={{
              background: '#FFF7ED',
              border: '2px dashed #D97706',
              borderRadius: 12,
              padding: '10px 12px',
              fontSize: 12,
              color: '#7C2D12',
              lineHeight: 1.5,
              marginTop: 12,
            }}
          >
            Votre commande reçoit son numéro tout de suite et part à valider chez votre chef ou
            votre responsable.
          </div>
        )}
        {envoi.erreur && (
          <div role="alert" style={alerte}>
            {envoi.erreur}
          </div>
        )}
        <button
          disabled={envoi.enCours || lignes.length === 0}
          onClick={envoie}
          style={{
            ...bouton(C.green),
            width: '100%',
            marginTop: 12,
            fontSize: 16,
            padding: '15px 14px',
            opacity: lignes.length === 0 ? 0.5 : 1,
          }}
        >
          {envoi.enCours ? 'Envoi…' : voitLesPrix ? '✓ Envoyer la commande' : 'Envoyer à valider'}
        </button>
      </div>
    </>
  );
}
